import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, Stack, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { createLiveVideoRequest, updateLiveVideoRequest } from "../../../../modules/admin-modules/liveVideos/Actions";
import { closeAddLiveVideo } from "../../../../modules/admin-modules/models/Actions";

/** API datetime → value for input[type=datetime-local] */
function apiDateToDatetimeLocal(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return "";
}

/** datetime-local → Laravel-friendly "Y-m-d H:i:s" */
function datetimeLocalToApi(val) {
  if (val == null || String(val).trim() === "") return null;
  const raw = String(val).trim();
  const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
  return withSeconds.replace("T", " ");
}

const AddLiveVideo = ({ isAdd, data, setSelectedData }) => {
  const openChapter = useSelector((state) => state.model.isOpen34);
  const isLoading = useSelector((state) => state.adminLiveVideos.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const chaptersLoading = useSelector((state) => state.chapters.isLoading);

  const dispatch = useDispatch();
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);

  const activeChapters = useMemo(
    () =>
      Array.isArray(chapters)
        ? chapters.filter((c) => Number(c?.active) === 1)
        : [],
    [chapters]
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: !isAdd
      ? {
        ...data,
        title: data?.title,
        zoom_link: data?.zoom_link,
        chapter_id: data?.chapter_id ?? data?.chapter?.id,
        start_time: apiDateToDatetimeLocal(data?.start_time),
      }
      : {},
    shouldUnregister: true,
  });

  const chapterId = watch("chapter_id");

  const chapterOptions = useMemo(() => {
    const opts = activeChapters.map((c) => ({ value: c.id, label: c.name }));
    const selectedId = chapterId ?? data?.chapter_id ?? data?.chapter?.id;
    if (
      selectedId != null &&
      selectedId !== "" &&
      !opts.some((o) => String(o.value) === String(selectedId))
    ) {
      const match = (Array.isArray(chapters) ? chapters : []).find(
        (c) => String(c.id) === String(selectedId)
      );
      if (match) {
        opts.unshift({ value: match.id, label: match.name });
      }
    }
    return opts;
  }, [
    activeChapters,
    chapters,
    chapterId,
    data?.chapter_id,
    data?.chapter?.id,
  ]);

  useEffect(() => {
    if (openChapter) {
      dispatch(getAllChaptersRequest());
    }
  }, [openChapter, dispatch]);

  useEffect(() => {
    if (!openChapter) return;
    if (!isAdd && data?.id) {
      reset({
        title: data?.title,
        zoom_link: data?.zoom_link,
        chapter_id: data?.chapter_id ?? data?.chapter?.id,
        start_time: apiDateToDatetimeLocal(data?.start_time),
      });
    } else if (isAdd) {
      reset({});
    }
  }, [openChapter, isAdd, data?.id, data?.chapter_id, reset]);

  // After chapters load, map response chapter_id onto the select options.
  useEffect(() => {
    if (!openChapter || isAdd || !Array.isArray(chapters) || chapters.length === 0) {
      return;
    }
    const id = data?.chapter_id ?? data?.chapter?.id;
    if (id == null || id === "") return;
    const match = chapters.find((c) => String(c.id) === String(id));
    if (match) {
      setValue("chapter_id", match.id, { shouldDirty: false });
    }
  }, [
    openChapter,
    isAdd,
    chapters,
    data?.chapter_id,
    data?.chapter?.id,
    setValue,
  ]);

  const selectedChapterOption = useMemo(() => {
    if (chapterId == null || chapterId === "") return null;
    const fromOptions = chapterOptions.find(
      (o) => String(o.value) === String(chapterId)
    );
    if (fromOptions) return fromOptions;
    const match = (Array.isArray(chapters) ? chapters : []).find(
      (c) => String(c.id) === String(chapterId)
    );
    return match ? { value: match.id, label: match.name } : null;
  }, [chapterOptions, chapterId, chapters]);

  const selectStyles = useMemo(
    () => withAdminSelectMenuPortal(adminSelectStyles()),
    []
  );

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Live Session" : "Edit Live Session"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Title and meeting link for students
      </Text>
    </Box>
  );
  const onSubmit = async (values) => {
    const action = () => {
      dispatch(closeAddLiveVideo());
      setSelectedData({});
      reset();
    };
    const startTime = datetimeLocalToApi(values?.start_time);
    let formData = new FormData();
    formData.append("title", values?.title);
    formData.append("zoom_link", values?.zoom_link);
    if (startTime != null) {
      formData.append("start_time", startTime);
    }
    if (values?.chapter_id != null && values?.chapter_id !== "") {
      formData.append("chapter_id", values.chapter_id);
    }
    if (isAdd) {
      if (values?.chapter_id == null || values?.chapter_id === "") {
        toast.error("Please select a lesson (active chapter)");
        return;
      }
      dispatch(
        createLiveVideoRequest(
          {
            ...values,
            chapter_id: values.chapter_id,
            start_time: startTime,
          },
          action
        )
      );
    } else {
      formData.append("_method", "PUT");
      dispatch(updateLiveVideoRequest(data?.id, formData, action));
    }
  };
  const closeFunc = () => {
    dispatch(closeAddLiveVideo());
    setSelectedData({});
    reset();
  };
  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openChapter}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">

          <MainInput
            label="Session title"
            defaultValues={data?.title ?? data?.name}
            error={errors?.title?.message}
            placeholder="Live session name"
            register={register}
            name="title"
          />
          <MainInput
            label="Meeting link"
            defaultValues={data?.zoom_link}
            error={errors?.zoom_link?.message}
            placeholder="https://..."
            register={register}
            name="zoom_link"
          />
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Start time
            </FormLabel>
            <Input
              type="datetime-local"
              {...register("start_time")}
              defaultValue={apiDateToDatetimeLocal(data?.start_time)}
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              borderRadius="12px"
              border="1px solid"
              borderColor="var(--border-subtle)"
            />
          </FormControl>
          <FormControl>
            <FormLabel
              fontWeight="800"
              color="var(--text-primary)"
              fontSize="sm"
              mb={2}
            >
              Lesson
            </FormLabel>
            <Select
              styles={selectStyles}
              options={chapterOptions}
              value={selectedChapterOption}
              onChange={(opt) =>
                setValue("chapter_id", opt?.value ?? "", {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              placeholder="Select lesson…"
              isClearable
              isLoading={Boolean(chaptersLoading)}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              maxMenuHeight={240}
              noOptionsMessage={() =>
                chaptersLoading
                  ? "Loading lessons…"
                  : "No active lessons — activate a chapter in Lessons first"
              }
            />
          </FormControl>
          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddLiveVideo;
